import React, { useState } from "react";
import {
  InnocodeButton,
  useDialog,
  useInnocodeLoadingOverlay,
} from "@intsoft/innocode-core";
import { containsXss } from "../../../../utils/security/XssFilter";
import { ssoAdminApi } from "../../api";
import styles from "../../../../utils/modal/DetailModal.module.css";

const groupTypeOptions = [
  { value: "INTERNAL", label: "내부형 SSO" },
  { value: "FEDERATION", label: "연합형 SSO" },
];

const inputActionStyle = {
  flex: 1,
  display: "flex",
  alignItems: "center",
  gap: "8px",
  minWidth: 0,
};

const actionButtonStyle = {
  flexShrink: 0,
  minWidth: "96px",
};

const groupKeyPattern = /^[a-z0-9-]+$/;

const createInitialDuplicateState = () => ({
  groupKey: { checkedValue: "", duplicated: null },
  groupName: { checkedValue: "", duplicated: null },
});

const isInvalidText = (value) =>
  typeof value === "string" && value.trim() !== "" && containsXss(value);

const getFieldLabel = (fieldName) =>
  fieldName === "groupKey" ? "그룹 키" : "그룹명";

const getDuplicateValue = (fieldName, result) =>
  fieldName === "groupKey"
    ? Boolean(result?.groupKeyDuplicated ?? result?.duplicated)
    : Boolean(result?.groupNameDuplicated ?? result?.duplicated);

function ServiceGroupFormModal({
  tenantOptions,
  defaultOwnerTenantId,
  onClose,
  onSaved,
}) {
  const { showDialog } = useDialog();
  const { show: showLoading, hide: hideLoading } = useInnocodeLoadingOverlay();
  const [isNull, setIsNull] = useState(false);
  const [form, setForm] = useState({
    groupKey: "",
    groupName: "",
    ownerTenantId: defaultOwnerTenantId || tenantOptions[0]?.value || "",
    groupType: "INTERNAL",
    description: "",
  });
  const [duplicateCheck, setDuplicateCheck] = useState(createInitialDuplicateState);

  const resetDuplicateState = (fieldName) => {
    setDuplicateCheck((prev) => ({
      ...prev,
      [fieldName]: {
        checkedValue: "",
        duplicated: null,
      },
    }));
  };

  const handleChange = (name, value) => {
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === "groupKey" || name === "groupName") {
      resetDuplicateState(name);
    }
  };

  const validateDuplicateFieldInput = (fieldName, value) => {
    if (!value) {
      showDialog({
        iconType: "warning",
        type: "error",
        title: "입력 확인",
        message: `${getFieldLabel(fieldName)}를 입력해 주세요.`,
        confirmText: "확인",
      });
      return false;
    }

    if (isInvalidText(value)) {
      showDialog({
        iconType: "warning",
        type: "error",
        title: "입력 오류",
        message: "허용되지 않는 문자가 포함되어 있습니다.",
        confirmText: "확인",
      });
      return false;
    }

    if (fieldName === "groupKey" && !groupKeyPattern.test(value)) {
      showDialog({
        iconType: "warning",
        type: "error",
        title: "입력 오류",
        message: "그룹 키는 영문 소문자, 숫자, 하이픈(-)만 입력할 수 있습니다.",
        confirmText: "확인",
      });
      return false;
    }

    return true;
  };

  const runDuplicateCheck = async (fieldName, options = {}) => {
    const { silent = false } = options;
    const value = String(form[fieldName] || "").trim();

    if (!silent && !validateDuplicateFieldInput(fieldName, value)) {
      return { success: false, duplicated: null, value };
    }

    showLoading();

    try {
      const params =
        fieldName === "groupKey" ? { groupKey: value } : { groupName: value };
      const result = await ssoAdminApi.checkServiceGroupDuplicate(params);
      const duplicated = getDuplicateValue(fieldName, result);

      setDuplicateCheck((prev) => ({
        ...prev,
        [fieldName]: {
          checkedValue: value,
          duplicated,
        },
      }));

      if (!silent) {
        showDialog({
          iconType: duplicated ? "warning" : "check",
          type: duplicated ? "error" : "success",
          title: duplicated ? `${getFieldLabel(fieldName)} 중복` : "사용 가능",
          message: duplicated
            ? `이미 사용 중인 ${getFieldLabel(fieldName)}입니다.`
            : `사용 가능한 ${getFieldLabel(fieldName)}입니다.`,
          confirmText: "확인",
        });
      }

      return { success: true, duplicated, value };
    } catch (error) {
      showDialog({
        iconType: "warning",
        type: "error",
        title: `${getFieldLabel(fieldName)} 중복 확인 실패`,
        message:
          error?.response?.data?.message ||
          `${getFieldLabel(fieldName)} 중복 확인 중 오류가 발생했습니다.`,
        confirmText: "확인",
      });
      return { success: false, duplicated: null, value };
    } finally {
      hideLoading();
    }
  };

  const handleDuplicateCheck = async (fieldName) => {
    await runDuplicateCheck(fieldName);
  };

  const handleSave = async () => {
    const trimmedGroupKey = form.groupKey.trim();
    const trimmedGroupName = form.groupName.trim();

    if (!trimmedGroupKey || !trimmedGroupName || !form.ownerTenantId) {
      setIsNull(true);
      showDialog({
        iconType: "warning",
        type: "error",
        title: "입력 확인",
        message: "그룹 키, 그룹명, 소유 테넌트를 입력해 주세요.",
        confirmText: "확인",
      });
      return;
    }

    if (
      isInvalidText(trimmedGroupKey) ||
      isInvalidText(trimmedGroupName) ||
      isInvalidText(form.description)
    ) {
      setIsNull(false);
      showDialog({
        iconType: "warning",
        type: "error",
        title: "입력 오류",
        message: "허용되지 않는 문자가 포함되어 있습니다.",
        confirmText: "확인",
      });
      return;
    }

    if (!groupKeyPattern.test(trimmedGroupKey)) {
      setIsNull(false);
      showDialog({
        iconType: "warning",
        type: "error",
        title: "입력 오류",
        message: "그룹 키는 영문 소문자, 숫자, 하이픈(-)만 입력할 수 있습니다.",
        confirmText: "확인",
      });
      return;
    }

    const groupKeyCheck = await runDuplicateCheck("groupKey", { silent: true });
    if (!groupKeyCheck.success) {
      return;
    }
    if (groupKeyCheck.duplicated) {
      showDialog({
        iconType: "warning",
        type: "error",
        title: "그룹 키 중복",
        message: "이미 사용 중인 그룹 키입니다. 다른 그룹 키를 입력해 주세요.",
        confirmText: "확인",
      });
      return;
    }

    const groupNameCheck = await runDuplicateCheck("groupName", { silent: true });
    if (!groupNameCheck.success) {
      return;
    }
    if (groupNameCheck.duplicated) {
      showDialog({
        iconType: "warning",
        type: "error",
        title: "그룹명 중복",
        message: "이미 사용 중인 그룹명입니다. 다른 그룹명을 입력해 주세요.",
        confirmText: "확인",
      });
      return;
    }

    setIsNull(false);
    showLoading();

    try {
      await ssoAdminApi.createServiceGroup({
        groupKey: trimmedGroupKey,
        groupName: trimmedGroupName,
        ownerTenantId: form.ownerTenantId,
        groupType: form.groupType,
        description: form.description.trim(),
      });

      showDialog({
        iconType: "check",
        type: "success",
        title: "생성 완료",
        message: "SSO 서비스 그룹을 생성했습니다.",
        confirmText: "확인",
        onConfirm: () => {
          onSaved();
          onClose();
        },
      });
    } catch (error) {
      showDialog({
        iconType: "warning",
        type: "error",
        title: "생성 실패",
        message:
          error?.response?.data?.message ||
          "SSO 서비스 그룹 생성 중 오류가 발생했습니다.",
        confirmText: "확인",
      });
    } finally {
      hideLoading();
    }
  };

  return (
    <div>
      <ul className={styles.layer__listWrap}>
        <li className={styles.layer__list}>
          <p className={styles.layer__listTit}>그룹 키</p>
          <div style={inputActionStyle}>
            <input
              className={styles.layer__listInput}
              type="text"
              value={form.groupKey}
              onChange={(event) => handleChange("groupKey", event.target.value)}
              placeholder="예: ownera-core-services"
            />
            <InnocodeButton
              size="sm"
              theme="line"
              designType="primary"
              style={actionButtonStyle}
              onClick={() => handleDuplicateCheck("groupKey")}
            >
              중복 확인
            </InnocodeButton>
          </div>
        </li>

        <li className={styles.layer__list}>
          <p className={styles.layer__listTit}>그룹명</p>
          <div style={inputActionStyle}>
            <input
              className={styles.layer__listInput}
              type="text"
              value={form.groupName}
              onChange={(event) => handleChange("groupName", event.target.value)}
              placeholder="예: 코어 서비스 그룹"
            />
            <InnocodeButton
              size="sm"
              theme="line"
              designType="primary"
              style={actionButtonStyle}
              onClick={() => handleDuplicateCheck("groupName")}
            >
              중복 확인
            </InnocodeButton>
          </div>
        </li>

        <li className={styles.layer__list}>
          <p className={styles.layer__listTit}>소유 테넌트</p>
          <select
            className={styles.layer__listInput}
            value={form.ownerTenantId}
            onChange={(event) =>
              handleChange("ownerTenantId", event.target.value || "")
            }
          >
            {tenantOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </li>

        <li className={styles.layer__list}>
          <p className={styles.layer__listTit}>그룹 유형</p>
          <select
            className={styles.layer__listInput}
            value={form.groupType}
            onChange={(event) =>
              handleChange("groupType", event.target.value || "INTERNAL")
            }
          >
            {groupTypeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </li>

        <li className={styles.layer__list}>
          <p className={styles.layer__listTit}>설명</p>
          <textarea
            className={styles.layer__listInput}
            value={form.description}
            onChange={(event) => handleChange("description", event.target.value)}
            placeholder="예: 핵심 연동 서비스 묶음"
            rows={4}
          />
        </li>
      </ul>

      {isNull ? (
        <div className={`${styles.layer__text} ${styles.warning}`}>
          빈 값을 확인해 주세요.
        </div>
      ) : null}

      <div className={styles.layer__saveBtnBox}>
        <InnocodeButton
          size="md"
          designType="primary"
          fullWidth={true}
          className={styles.layer__saveBtn}
          onClick={handleSave}
        >
          생성
        </InnocodeButton>
      </div>
    </div>
  );
}

export default ServiceGroupFormModal;
